/**
 * NobleCard - Metal-Textured Card Component
 * 
 * Industrial-grade card with neumorphic depth and metal texture
 * Features:
 * - 8dp extruded emboss effect
 * - 1px etched border for industrial feel
 * - Hover lift animation
 * - Semi-transparent texture overlay
 * - Multiple variants for different contexts
 * 
 * @version 1.0.0
 * @date December 1, 2025
 */

import React from 'react';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { NobleTokens } from '../../../theme/noble-tokens';

export interface NobleCardProps {
  variant?: 'default' | 'equipment' | 'kpi' | 'stats' | 'general';
  hover?: boolean;
  gradient?: 'command' | 'steel' | 'alert' | 'success' | 'none';
  padding?: 'small' | 'medium' | 'large';
  title?: React.ReactNode;
  extra?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

const StyledMotionDiv = styled(motion.div)<{
  $variant: 'default' | 'equipment' | 'kpi' | 'stats';
  $hover: boolean;
  $gradient: 'command' | 'steel' | 'alert' | 'success' | 'none';
  $padding: 'small' | 'medium' | 'large';
}>`
  /* Base Card Styles */
  background: ${({ $gradient }) => {
    switch ($gradient) {
      case 'command':
        return NobleTokens.colors.gradients.command;
      case 'steel':
        return NobleTokens.colors.gradients.steel;
      case 'alert':
        return NobleTokens.colors.gradients.alert;
      case 'success':
        return NobleTokens.colors.gradients.success;
      default:
        return NobleTokens.colors.gradients.surface;
    }
  }};
  
  border: 1px solid ${NobleTokens.colors.border.etched};
  border-radius: ${NobleTokens.borderRadius.lg};
  box-shadow: ${NobleTokens.shadows.neumorphicRaised};
  transition: all ${NobleTokens.animation.duration.standard} ${NobleTokens.animation.easing.easeOut};
  
  /* Padding Variants */
  ${({ $padding }) => {
    switch ($padding) {
      case 'small':
        return `padding: ${NobleTokens.components.card.padding.small};`;
      case 'large':
        return `padding: ${NobleTokens.components.card.padding.large};`;
      default:
        return `padding: ${NobleTokens.components.card.padding.medium};`;
    }
  }}
  
  /* Hover Effect */
  ${({ $hover }) =>
    $hover &&
    `
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8);
      cursor: pointer;
    }
  `}
  
  /* Variant Specific Styles */
  ${({ $variant, $gradient }) => {
    if ($gradient !== 'none') {
      return `
        color: white;
        
        * {
          color: white !important;
        }
        
        .ant-typography {
          color: white !important;
        }
      `;
    }
    
    switch ($variant) {
      case 'equipment':
        return `
          position: relative;
          overflow: hidden;
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 100px;
            height: 100px;
            background: radial-gradient(circle, rgba(92, 107, 125, 0.05) 0%, transparent 70%);
            pointer-events: none;
          }
        `;
      
      case 'kpi':
        return `
          min-height: 140px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          position: relative;
          overflow: hidden;
          
          &::after {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
            pointer-events: none;
          }
        `;
      
      case 'stats':
        return `
          background: ${NobleTokens.colors.background.level1Light};
          border-left: 4px solid ${NobleTokens.colors.primary.commandBlue};
        `;
      
      default:
        return '';
    }
  }}
`;

const CardHeader = styled.div<{ $hasGradient: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${NobleTokens.spacing.md};
  padding-bottom: ${NobleTokens.spacing.sm};
  border-bottom: 1px solid ${({ $hasGradient }) => 
    $hasGradient ? 'rgba(255, 255, 255, 0.2)' : NobleTokens.colors.border.light};
  
  h3, h4 {
    margin: 0;
    font-weight: ${NobleTokens.typography.fontWeight.semibold};
    color: ${({ $hasGradient }) => 
      $hasGradient ? 'white' : NobleTokens.colors.text.primary};
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NobleTokens.spacing.sm};
`;

const CardFooter = styled.div<{ $hasGradient: boolean }>`
  margin-top: ${NobleTokens.spacing.md};
  padding-top: ${NobleTokens.spacing.sm};
  border-top: 1px solid ${({ $hasGradient }) => 
    $hasGradient ? 'rgba(255, 255, 255, 0.2)' : NobleTokens.colors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${NobleTokens.typography.fontSize.bodySmall};
  color: ${({ $hasGradient }) => 
    $hasGradient ? 'rgba(255, 255, 255, 0.85)' : NobleTokens.colors.text.secondary};
`;

/**
 * NobleCard Component
 */
export const NobleCard: React.FC<NobleCardProps> = ({
  variant: variantProp = 'default',
  hover = false,
  gradient = 'none',
  padding = 'medium',
  children,
  title,
  extra,
  style,
  onClick,
  ...rest
}) => {
  const variant = variantProp === 'general' ? 'default' : variantProp;
  const hasGradient = gradient !== 'none';
  const isClickable = !!onClick || hover;

  return (
    <StyledMotionDiv
      $variant={variant}
      $hover={isClickable}
      $gradient={gradient}
      $padding={padding}
      onClick={onClick}
      /* MODERN UPGRADE: prevent horizontal overflow in narrow grids */
      style={{ maxWidth: '100%', boxSizing: 'border-box', ...style }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      whileHover={
        isClickable
          ? {
              y: -4,
            }
          : undefined
      }
    >
      {(title || extra) && (
        <CardHeader $hasGradient={hasGradient}>
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {extra}
        </CardHeader>
      )}
      
      <CardContent>{children}</CardContent>
    </StyledMotionDiv>
  );
};

/**
 * Equipment/Asset Card Variant
 * Pre-configured for equipment displays
 */
export const NobleEquipmentCard: React.FC<NobleCardProps & {
  equipmentId?: string;
  equipmentName?: string;
  status?: 'operational' | 'warning' | 'critical' | 'maintenance';
  location?: string;
  nextMaintenance?: string;
}> = ({
  equipmentId,
  equipmentName,
  status = 'operational',
  location,
  nextMaintenance,
  children,
  ...props
}) => {
  const statusColors = {
    operational: NobleTokens.colors.semantic.operationalGreen,
    warning: NobleTokens.colors.semantic.warningAmber,
    critical: NobleTokens.colors.semantic.criticalRed,
    maintenance: NobleTokens.colors.semantic.maintenanceYellow,
  };

  return (
    <NobleCard variant="equipment" hover {...props}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: NobleTokens.spacing.sm }}>
        {equipmentId && (
          <span
            style={{
              fontFamily: NobleTokens.typography.fontFamily.mono,
              fontSize: NobleTokens.typography.fontSize.bodySmall,
              color: NobleTokens.colors.text.secondary,
            }}
          >
            {equipmentId}
          </span>
        )}
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: statusColors[status],
            boxShadow: `0 0 8px ${statusColors[status]}`,
          }}
        />
      </div>
      
      {equipmentName && (
        <h4 style={{ margin: `${NobleTokens.spacing.xs} 0` }}>{equipmentName}</h4>
      )}
      
      {children}
      
      {(location || nextMaintenance) && (
        <CardFooter $hasGradient={false}>
          {location && <span>📍 {location}</span>}
          {nextMaintenance && (
            <span
              style={{
                color:
                  new Date(nextMaintenance) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                    ? NobleTokens.colors.semantic.warningAmber
                    : NobleTokens.colors.text.secondary,
              }}
            >
              Next: {nextMaintenance}
            </span>
          )}
        </CardFooter>
      )}
    </NobleCard>
  );
};

/**
 * KPI Stat Card Variant
 * Pre-configured for dashboard KPIs with gradients
 */
export const NobleKPICard: React.FC<NobleCardProps & {
  value: string | number;
  label: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
}> = ({
  value,
  label,
  trend,
  trendValue,
  icon,
  gradient = 'command',
  ...props
}) => {
  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→',
  };

  const trendColors = {
    up: 'rgba(6, 214, 160, 1)',
    down: 'rgba(230, 57, 70, 1)',
    neutral: 'rgba(255, 255, 255, 0.7)',
  };

  return (
    <NobleCard variant="kpi" gradient={gradient} padding="large" {...props}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div
            style={{
              fontSize: '36px',
              fontWeight: NobleTokens.typography.fontWeight.bold,
              lineHeight: 1.2,
              marginBottom: NobleTokens.spacing.xs,
            }}
          >
            {value}
          </div>
          <div
            style={{
              fontSize: NobleTokens.typography.fontSize.body,
              fontWeight: NobleTokens.typography.fontWeight.medium,
              opacity: 0.9,
            }}
          >
            {label}
          </div>
          {trend && trendValue && (
            <div
              style={{
                fontSize: NobleTokens.typography.fontSize.bodySmall,
                marginTop: NobleTokens.spacing.xs,
                color: trendColors[trend],
              }}
            >
              {trendIcons[trend]} {trendValue}
            </div>
          )}
        </div>
        {icon && (
          <div
            style={{
              fontSize: '32px',
              opacity: 0.9,
            }}
          >
            {icon}
          </div>
        )}
      </div>
    </NobleCard>
  );
};

export default NobleCard;

